/**
 * Ambient signal registry — per-entity-type renderers that ride along
 * with every entity render.
 *
 * The framework's entity-render primitives (EntityRef, member-grid rows,
 * future entity-card densities) expose an optional `ambient_signal` slot.
 * Host apps register a Component per entity_type via this registry, and
 * the framework dispatches to that Component whenever an entity is
 * rendered with hydrated signal data.
 *
 * The host's Component is responsible for rendering at three densities —
 * "chip" (~18px, inline mentions), "row" (~28px, list rows), and "card"
 * (full evidence card). The framework just guarantees the slot exists in
 * a consistent visual position and that the renderer is called with the
 * hydrated signal.
 *
 * This is the slot abstraction that makes ambient entity intelligence
 * extractable: CongressMCP registers `member` → CascadeSignatureBadge,
 * a future sales-CRM adopter would register `deal` → their own indicator,
 * and so on. The pattern (per-entity ambient intelligence as a baked-in
 * slot, not a separate feature page) is the load-bearing piece.
 *
 * Hydration is server-side, not lazy. Hosts populate `ambient_signal`
 * via prop drilling (typically: compose_panel inlines the data into the
 * persisted PanelSpec; renderers receive it ready to render). The
 * framework doesn't provide a client-side fetch hook — adding one would
 * cause per-badge HTTP roundtrips for member lists.
 */
import type { ComponentType } from "react";
// React 19 types no longer declare a global JSX namespace.
import type { JSX } from "react";

export type AmbientSignalDensity = "chip" | "row" | "card";

export interface AmbientSignalProps<T = unknown> {
  /** Domain entity type, e.g. "member", "deal", "ticket". The registry
   *  dispatches by this key. */
  entity_type: string;
  /** Stable identifier for the entity (e.g. bioguide, account id). */
  entity_id: string;
  /** Render density — host renderer decides how to surface at each. */
  density: AmbientSignalDensity;
  /** Hydrated signal payload — shape is host-defined per entity_type. */
  ambient_signal: T;
}

export interface AmbientSignalRendererSpec<T = unknown> {
  /** Entity type this renderer handles. */
  entity_type: string;
  /** Optional human label for dev/debug surfaces. */
  label?: string;
  /** React component called with AmbientSignalProps. */
  Component: ComponentType<AmbientSignalProps<T>>;
}

export class AmbientSignalRegistry {
  private map = new Map<string, AmbientSignalRendererSpec<unknown>>();

  register<T>(spec: AmbientSignalRendererSpec<T>): void {
    if (this.map.has(spec.entity_type)) {
       
      console.warn(
        `[AmbientSignalRegistry] Overwriting renderer for "${spec.entity_type}"`,
      );
    }
    this.map.set(spec.entity_type, spec as AmbientSignalRendererSpec<unknown>);
  }

  get(entity_type: string): AmbientSignalRendererSpec<unknown> | undefined {
    return this.map.get(entity_type);
  }

  has(entity_type: string): boolean {
    return this.map.has(entity_type);
  }

  list(): AmbientSignalRendererSpec<unknown>[] {
    return Array.from(this.map.values());
  }
}

/** Default singleton — most apps only need one. */
export const defaultAmbientRegistry = new AmbientSignalRegistry();

/** Convenience binding to the default singleton. */
export function registerAmbientSignalRenderer<T>(
  spec: AmbientSignalRendererSpec<T>,
): void {
  defaultAmbientRegistry.register(spec);
}

/**
 * Slot component — renders the registered ambient signal for an entity
 * at a given density, or null if either (a) no renderer is registered
 * for that entity_type, or (b) ambient_signal is undefined/null.
 *
 * Consumers drop this next to entity identity in their render:
 *
 *   <span>
 *     {member.name}
 *     <AmbientSignalSlot
 *       entity_type="member"
 *       entity_id={member.bioguide}
 *       ambient_signal={member.cascade_signature}
 *       density="chip"
 *     />
 *   </span>
 *
 * Returns null when no signal is hydrated — the slot is invisible in
 * that case (no placeholder, no loading state). The host hydrates
 * server-side; if it's missing, the substrate didn't have it.
 */
export function AmbientSignalSlot({
  entity_type,
  entity_id,
  ambient_signal,
  density,
}: {
  entity_type: string;
  entity_id: string;
  ambient_signal: unknown;
  density: AmbientSignalDensity;
}): JSX.Element | null {
  if (ambient_signal == null) return null;
  const spec = defaultAmbientRegistry.get(entity_type);
  if (!spec) return null;
  const Comp = spec.Component;
  return (
    <Comp
      entity_type={entity_type}
      entity_id={entity_id}
      density={density}
      ambient_signal={ambient_signal}
    />
  );
}
